import React from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import {
  IconButton,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
  TextField,
  Tooltip,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import AppConfirmDialog from "components/Shared/AppConfirmDialog";
import { useLazyListShipmentForReturnedQuery } from "store/services/reportApi";
import { exportExcelFile } from "utils/exportExcelFile";
import moment from "moment";
import { FileDownloadOutlined } from "@mui/icons-material";

const EXPORT_DATA_INITIAL_STATE = {
  expiryFilter: "week",
  dateRange: [],
};

const ExportShipmentReturnExcel = ({ isBusy = false }) => {
  const [showDialog, setShowDialog] = React.useState(false);
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    defaultValues: EXPORT_DATA_INITIAL_STATE,
  });

  const selectedFilter = watch("expiryFilter");
  const [fetchShipment, { isLoading: isFetching }] = useLazyListShipmentForReturnedQuery();

const handleDownload = async (data) => {
  try {
    let params = {
      limit: Number.MAX_SAFE_INTEGER // Get all records for export
    };
    let fileNameParts = ["Returned_Shipment_Report"];

    if (data.expiryFilter === "custom" && data.dateRange?.length === 2) {
      params.fromDate = moment(data.dateRange[0]).startOf('day').format("YYYY-MM-DD");
      params.toDate = moment(data.dateRange[1]).endOf('day').format("YYYY-MM-DD");
      fileNameParts.push(
        `${moment(data.dateRange[0]).format("MMM_DD")}_to_${moment(data.dateRange[1]).format("MMM_DD")}`
      );
    } else if (data.expiryFilter === "week" || data.expiryFilter === "month") {
      const days = data.expiryFilter === "week" ? 7 : 30;
      const toDate = moment().endOf('day'); // Include entire end day
      const fromDate = moment().subtract(days, "days").startOf('day'); // Include entire start day
      params.fromDate = fromDate.format("YYYY-MM-DD");
      params.toDate = toDate.format("YYYY-MM-DD");
      fileNameParts.push(data.expiryFilter === "week" ? "Last_Week" : "Last_Month");
    }

    const responseData = await fetchShipment(params).unwrap();

    if (!responseData?.data || responseData.data.length === 0) {
      toast.info("No returned shipments found for the selected criteria.");
      return;
    }

    const formattedData = responseData.data.map((row, index) => {
      const shiId = row?.shipmentId?.replace(/(.{4})/g, "$1-").slice(0, -1);
      const displayId = `${shiId}${row.IsReturning ? " (R)" : ""}`;
      
      return {
        SN: index + 1,
        "Shipment ID": displayId || "N/A",
        "Sender": row?.customerId?.customerUser?.name 
          ? `CUS: ${row.customerId.customerUser.name}`
          : row?.vendor?.shopName
            ? `VEN: ${row.vendor.shopName}`
            : "N/A",
        "Receiver Name": row?.receiver?.name || "N/A",
        "Receiver Phone": row?.receiver?.phone || "N/A",
        "From Branch": row?.sourceBranch?.name || "N/A",
        "To Branch": row?.destinationBranch?.name || "N/A",
        "Created At": row?.createdAt
          ? moment(row.createdAt).format("YYYY-MM-DD HH:mm:ss")
          : "N/A",
        "Updated At": row?.updatedAt
          ? moment(row.updatedAt).format("YYYY-MM-DD HH:mm:ss")
          : "N/A"
      };
    });

    const cleanFileNameParts = fileNameParts
      .map((part) => part.replace(/[^a-zA-Z0-9_]/g, "").trim())
      .filter((part) => part.length > 0);

    const fileName = cleanFileNameParts.join("_");

    exportExcelFile({
      sheetData: formattedData,
      fileName: fileName,
    });

    toast.success("Report downloaded successfully");
    handleReset();
  } catch (error) {
    toast.error("Error downloading report: " + (error.message || "Unknown error"));
  }
};

  const handleReset = () => {
    setShowDialog(false);
    reset(EXPORT_DATA_INITIAL_STATE);
  };

  return (
    <>
      <Tooltip title="Export Excel" arrow>
        <IconButton
          color="success"
          onClick={() => setShowDialog(true)}
          disabled={isBusy}
        >
          <FileDownloadOutlined />
        </IconButton>
      </Tooltip>

      <AppConfirmDialog
        open={showDialog}
        onClose={handleReset}
        onConfirm={handleSubmit(handleDownload)}
        title="Download Returned Shipment Report"
        dialogIcon={() => <FileDownloadOutlined color="success" />}
        confirmText="Download"
        confirmDisabled={isFetching || isBusy}
      >
        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <FormLabel component="legend">Filter by Updated Date</FormLabel>
          <Controller
            name="expiryFilter"
            control={control}
            render={({ field }) => (
              <RadioGroup {...field} row>
                <FormControlLabel
                  value="week"
                  control={<Radio />}
                  label="Last Week"
                />
                <FormControlLabel
                  value="month"
                  control={<Radio />}
                  label="Last Month"
                />
                <FormControlLabel
                  value="custom"
                  control={<Radio />}
                  label="Custom Date Range"
                />
              </RadioGroup>
            )}
          />
        </FormControl>

        {selectedFilter === "custom" && (
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Box sx={{ mb: 2, display: "flex", flexDirection: "column", gap: 2 }}>
              <Controller
                name="dateRange[0]"
                control={control}
                rules={{
                  validate: (value) => {
                    if (selectedFilter === "custom" && !value) {
                      return "Please select a start date";
                    }
                    return true;
                  },
                }}
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <DesktopDatePicker
                    label="Start Date"
                    inputFormat="MM/DD/YYYY"
                    value={value || null}
                    onChange={(date) => onChange(date)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                )}
              />
              <Controller
                name="dateRange[1]"
                control={control}
                rules={{
                  validate: (value) => {
                    if (selectedFilter === "custom" && !value) {
                      return "Please select an end date";
                    }
                    const startDate = watch("dateRange[0]");
                    if (
                      selectedFilter === "custom" &&
                      startDate &&
                      value &&
                      moment(value).isBefore(moment(startDate))
                    ) {
                      return "End date must be after start date";
                    }
                    return true;
                  },
                }}
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <DesktopDatePicker
                    label="End Date"
                    inputFormat="MM/DD/YYYY"
                    value={value || null}
                    onChange={(date) => onChange(date)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                    shouldDisableDate={(date) =>
                      watch("dateRange[0]") && date < moment(watch("dateRange[0]"))
                    }
                    disabled={!watch("dateRange[0]")}
                  />
                )}
              />
            </Box>
          </LocalizationProvider>
        )}
      </AppConfirmDialog>
    </>
  );
};

export default ExportShipmentReturnExcel;